import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FileText, Play } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { CardContent, CardIcon } from '@/components/ui/card'
import { useNetwork } from '@/contexts/NetworkContext'
import { useUrlSearch } from '@/hooks/useUrlSearch'
import { ReportsApi, isUnfinished } from '@/services/reports/reportsApi'
import type { ListSort } from '@/services/core/paging'
import { AppShell } from '../../components/AppShell'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { ListPagination } from '../../components/ListPagination'
import { PageSizeSelect } from '../../components/PageSizeSelect'
import { SearchInput } from '../../components/SearchInput'
import { ReportExecutionTable } from '../../components/reports/ReportExecutionTable'
import { RequestReportDialog } from '../../components/reports/RequestReportDialog'

/** How often a page with unfinished reports on it is checked, in milliseconds. */
const POLL_MS = 5000

/** Every report produced in the network — run by hand or by a schedule — newest first. */
export function ReportsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { scopeKey, scopeReady } = useNetwork()
  const [search, setSearch] = useUrlSearch()
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<ListSort | null>(null)
  const [pageSize, setPageSize] = useState(25)
  const [requestOpen, setRequestOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['report-executions', 'page', scopeKey, search, page, pageSize, sort],
    queryFn: () => ReportsApi.getExecutionPage({ page, pageSize, search, sort }),
    enabled: scopeReady,
    // Reports are built in the background; a page showing one that is still
    // being built keeps checking until it lands.
    refetchInterval: (query) =>
      (query.state.data?.results ?? []).some((e) => isUnfinished(e.status)) ? POLL_MS : false,
  })
  const executions = data?.results ?? []
  const totalCount = data?.totalCount ?? 0

  const refilter = (apply: () => void) => {
    apply()
    setPage(1)
  }

  const handleSort = (next: ListSort | null) => {
    setSort(next)
    setPage(1)
  }

  return (
    <AppShell crumbs={[{ label: t('reportsModule.title') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('reportsModule.title')}</h1>
            <p>{t('reportsModule.subtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <Button onClick={() => setRequestOpen(true)}>
              <Play className="size-4" /> {t('reportsModule.requestReport')}
            </Button>
          </div>
        </div>

        <ListCard tone="insight">
          <ListCardHeader
            title={
              <>
                <CardIcon icon={FileText} />
                {t('reportsModule.title')}
              </>
            }
            count={totalCount}
            actions={
              <PageSizeSelect
                value={pageSize}
                onChange={(size) => refilter(() => setPageSize(size))}
              />
            }
            controls={<SearchInput value={search} onChange={(v) => refilter(() => setSearch(v))} />}
          />

          <CardContent className="p-0">
            <ReportExecutionTable
              executions={executions}
              loading={isLoading}
              sort={sort}
              onSortChange={handleSort}
            />
          </CardContent>

          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            summary={t('reportsModule.reportCount', { count: totalCount })}
          />
        </ListCard>
      </div>

      <RequestReportDialog
        open={requestOpen}
        onOpenChange={setRequestOpen}
        onRequested={() => {
          // The new report is the newest, so it shows on the first page.
          setPage(1)
          queryClient.invalidateQueries({ queryKey: ['report-executions'] })
        }}
      />
    </AppShell>
  )
}
